import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "../lib/language";

const HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_8a19358c-b38c-426f-9a7e-f8fe7efffea4/artifacts/rpbmsptf_B98721E3-6189-42F0-BB4E-B10BC6D3B2FD.PNG";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim());

export const Hero = () => {
  const { t, lang } = useLanguage();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!isValidEmail(email)) {
      toast.error(t.toast.invalid, { duration: 3500 });
      return;
    }

    setLoading(true);

    try {
      const res = await axios.post(`${API}/newsletter`, {
        email: email.trim(),
        locale: lang,
      });

      const data = res.data || {};

      if (data.success) {
        toast.success(t.toast.success_title, {
          description: t.toast.success_desc,
          duration: 4500,
        });
      } else {
        toast.error(data.error || t.toast.error, {
          duration: 4000,
        });
      }

      setEmail("");
    } catch (err) {
      console.error("Newsletter error", err);

      toast.error(t.toast.error, {
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative w-full"
    >
      <div className="relative w-full h-[100svh] min-h-[640px] overflow-hidden grain">
        <img
          src={HERO_IMAGE}
          alt="RESILIÉ — couple at sunset wearing the collection"
          loading="eager"
          className="absolute inset-0 w-full h-full object-cover image-reveal"
          data-testid="hero-image"
        />

        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(10,10,10,0.35) 0%, rgba(10,10,10,0.05) 28%, rgba(10,10,10,0) 55%, rgba(10,10,10,0.55) 100%)",
          }}
          aria-hidden="true"
        />

        <div className="absolute top-24 md:top-28 left-0 right-0 px-6 md:px-12 fade-in delay-300">
          <p
            data-testid="hero-eyebrow"
            className="text-center font-sans text-[10px] md:text-[11px] tracking-[0.5em] uppercase text-white/85"
          >
            {t.hero.eyebrow}
          </p>
        </div>

        <div className="absolute inset-x-0 bottom-0 px-6 md:px-12 pb-16 md:pb-24">
          <div className="max-w-xl md:max-w-lg lg:max-w-xl text-left">
            <h1
              data-testid="hero-title"
              className="font-serif text-white font-light leading-[0.95] tracking-tight fade-up delay-500
                         text-[40px] sm:text-5xl md:text-6xl lg:text-[80px]"
            >
              <span className="block">{t.hero.title_pre}</span>

              <span className="block italic font-light opacity-95">
                — {t.hero.title_em}
              </span>
            </h1>

            <p
              data-testid="hero-lead"
              className="mt-5 md:mt-7 max-w-md font-sans text-sm md:text-base leading-relaxed text-white/85 fade-up delay-700"
            >
              {t.hero.lead}
            </p>

            <form
              onSubmit={onSubmit}
              data-testid="newsletter-form"
              className="mt-8 md:mt-10 max-w-md fade-up delay-900"
              noValidate
            >
              <div className="flex items-end gap-4 md:gap-6">
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.hero.placeholder}
                  className="input-line on-dark flex-1"
                  data-testid="newsletter-email-input"
                  aria-label={t.hero.placeholder}
                />

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-line on-dark"
                  data-testid="newsletter-subscribe-btn"
                >
                  {loading ? t.hero.cta_loading : t.hero.cta}
                </button>
              </div>

              <p
                data-testid="newsletter-legal"
                className="mt-4 font-sans text-[10px] tracking-[0.18em] uppercase text-white/55"
              >
                {t.hero.legal}
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
